import React from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
// @rbac
import { useB2BPermissions } from '../../../../context/B2BPermissionsContext';
// @design-system
import TitleBanner from '../../../../design-system/components/Banners/TitleBanner';
import Select from '../../../../design-system/components/Form/Select/Select';
import Table from '../../../../design-system/components/Table/Table';
import LoaderContent from '../../../../design-system/components/Loader/LoaderContent';
import Toast from '../../../../design-system/components/Toast/Toast';
import NothingFound from '../../../../components/NothingFound/NothingFound';
// @material-core
import {
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  MenuItem,
  Icon,
  IconButton,
} from '@material-ui/core';
import moment from 'moment';
// @date-pickers (mismo patrón que Altas/Modificaciones)
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import dateFormat from '../../../../design-system/utils/dateFormat';
import { ISO_DATE_FORMAT } from '../../../../design-system/constants/date-formats';
// @thirdparty
import { isNil } from 'ramda';
// @local
import {
  contractsColumns,
  contractLogColumns,
} from '../ContractsColumns';
import {
  getContracts,
  getContractDetail,
  getContractLog,
  createContract,
  updateContract,
  MEMBERSHIP_CATALOG,
} from '../contractsMockService';
import { commonModuleStyles } from '../../../../assets/styles/jss/pages/common/CommonModuleStyles';

// Vistas internas del módulo, equivalentes a las "cards" de la propuesta visual.
const VIEWS = {
  LIST: 'list',
  DETAIL: 'detail',
  CREATE: 'create',
  LOG: 'log',
};

const STATUS_OPTIONS = [
  { id: 'Todas', label: 'Todas' },
  { id: 'Vigente', label: 'Vigente' },
  { id: 'Anterior', label: 'Anterior' },
];

const MODE_LABEL = {
  detailed: 'Con desglose',
  total_only: 'Solo total',
};

const formatValidity = (start, end) => {
  const fmt = (d) => (d ? moment(d).format('DD/MM/YYYY') : '—');
  return `${fmt(start)} – ${fmt(end)}`;
};

const ContractsListPage = () => {
  const classes = commonModuleStyles();
  // Mismo contrato de scope que el módulo B2B: las empresas vienen filtradas por
  // internal/external según la configuración del usuario.
  const {
    assignedCompanies,
    autoSelectCompany,
    assignedCompanyName,
  } = useB2BPermissions();

  const [view, setView] = React.useState(VIEWS.LIST);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({ toastProps: { message: '', open: false } });

  // Catálogo de empresas (scoped). Se arma desde el scope del usuario.
  const [companyItems, setCompanyItems] = React.useState([]);
  const [selectedCompany, setSelectedCompany] = React.useState('');
  const [selectedStatus, setSelectedStatus] = React.useState('Todas');

  const [rows, setRows] = React.useState([]);
  const [hasSearched, setHasSearched] = React.useState(false);

  // Detalle / bitácora del contrato seleccionado.
  const [detail, setDetail] = React.useState(null);
  const [logRows, setLogRows] = React.useState([]);

  // Construir el catálogo de empresas desde el scope (internal/external). Igual
  // que en Membresías: sin empresas asignadas, no hay nada que consultar.
  React.useEffect(() => {
    const items = (assignedCompanies || []).map((c) => ({
      id: c.company_name,
      label: c.company_name,
      value: c.company_name,
    }));
    setCompanyItems(items);

    // Autoselección: si el usuario tiene exactamente UNA empresa, se precarga y
    // se consulta directo (mismo comportamiento que el reporte de Membresías).
    if (autoSelectCompany) {
      setSelectedCompany(autoSelectCompany.company_name);
      fetchContracts(autoSelectCompany.company_name, 'Todas');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedCompanyName.join('|')]);

  const fetchContracts = async (companyTag, status) => {
    if (isNil(companyTag) || companyTag === '') {
      setRows([]);
      return;
    }
    try {
      setLoading(true);
      const { data } = await getContracts({ companyTag, status });
      setRows(Array.isArray(data) ? data : []);
      setHasSearched(true);
    } catch (err) {
      setToast({ toastProps: { severity: 'error', open: true, message: err?.message || 'Ops! Ha ocurrido un error' } });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (isNil(selectedCompany) || selectedCompany === '') {
      setToast({ toastProps: { severity: 'warning', open: true, message: 'Seleccione una empresa para consultar los contratos.' } });
      return;
    }
    fetchContracts(selectedCompany, selectedStatus);
  };

  const openDetail = async (contractId) => {
    try {
      setLoading(true);
      const { data } = await getContractDetail(contractId);
      setDetail(data);
      setView(VIEWS.DETAIL);
    } catch (err) {
      setToast({ toastProps: { severity: 'error', open: true, message: err?.message || 'Ops! Ha ocurrido un error' } });
    } finally {
      setLoading(false);
    }
  };

  const openLog = async (contractId) => {
    try {
      setLoading(true);
      const { data } = await getContractLog(contractId);
      setLogRows(Array.isArray(data) ? data : []);
      setView(VIEWS.LOG);
    } catch (err) {
      setToast({ toastProps: { severity: 'error', open: true, message: err?.message || 'Ops! Ha ocurrido un error' } });
    } finally {
      setLoading(false);
    }
  };

  // Celdas personalizadas de la tabla de listado (badge de estatus, modo, botón Ver).
  const listColumns = React.useMemo(
    () =>
      contractsColumns.map((col) => {
        if (col.name === 'validity') {
          return { ...col, format: (row) => formatValidity(row.start_date, row.end_date) };
        }
        if (col.name === 'status_badge') {
          return {
            ...col,
            format: (row) => (
              <Chip
                size="small"
                label={row.status}
                style={
                  row.status === 'Vigente'
                    ? { backgroundColor: '#ecfdf3', color: '#067647', fontWeight: 600 }
                    : { backgroundColor: '#f2f4f7', color: '#344054', fontWeight: 600 }
                }
              />
            ),
          };
        }
        if (col.name === 'mode_label') {
          return { ...col, format: (row) => MODE_LABEL[row.mode] || '—' };
        }
        if (col.name === 'actions') {
          return {
            ...col,
            format: (row) => (
              <Button color="primary" size="small" onClick={() => openDetail(row.id)}>
                Ver
              </Button>
            ),
          };
        }
        return col;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TitleBanner
            title="Contratos B2B - Listado"
            desc="Consulta y edita los contratos de cada empresa: total de licencias compradas y, cuando aplica, el desglose por membresía."
          />
        </Grid>

        {/* ───────────────────────── LISTADO ───────────────────────── */}
        {view === VIEWS.LIST && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={4}>
                    <Select
                      items={companyItems}
                      label="Empresa"
                      name="company"
                      value={selectedCompany}
                      onChange={(e) => setSelectedCompany(e.target.value)}
                      disabled={!companyItems.length || loading}
                      size="medium"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Select
                      items={STATUS_OPTIONS}
                      label="Estatus"
                      name="status"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      disabled={loading}
                      size="medium"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Box display="flex" style={{ gap: 12 }}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        disabled={loading || isNil(selectedCompany) || selectedCompany === ''}
                        style={{ height: 44 }}
                      >
                        Buscar
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setView(VIEWS.CREATE)}
                        disabled={loading}
                        style={{ height: 44 }}
                      >
                        Nuevo contrato
                      </Button>
                    </Box>
                  </Grid>
                </Grid>

                <LoaderContent loading={loading} minHeight={400}>
                  {isNil(selectedCompany) || selectedCompany === '' ? (
                    !loading && (
                      <NothingFound
                        width="200px"
                        caption="Seleccione una empresa para consultar los contratos."
                        subcaption="El listado se filtra por la empresa que tenga configurada (internal/external)."
                      />
                    )
                  ) : rows.length > 0 ? (
                    <Box mt={2}>
                      <Table columns={listColumns} rows={rows} loading={false} />
                    </Box>
                  ) : (
                    !loading &&
                    hasSearched && (
                      <NothingFound
                        width="200px"
                        caption="No se encontraron contratos para esta empresa."
                        subcaption="Prueba con otro estatus o crea un nuevo contrato."
                      />
                    )
                  )}
                </LoaderContent>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* ───────────────────────── DETALLE ───────────────────────── */}
        {view === VIEWS.DETAIL && detail && (
          <ContractDetail
            classes={classes}
            detail={detail}
            onBack={() => setView(VIEWS.LIST)}
            onLog={() => openLog(detail.id)}
            onSaved={(message) => {
              setToast({ toastProps: { severity: 'success', open: true, message } });
            }}
          />
        )}

        {/* ─────────────────────── NUEVO CONTRATO ─────────────────── */}
        {view === VIEWS.CREATE && (
          <ContractCreate
            classes={classes}
            companyItems={companyItems}
            onCancel={() => setView(VIEWS.LIST)}
            onCreated={(message) => {
              setToast({ toastProps: { severity: 'success', open: true, message } });
              setView(VIEWS.LIST);
              if (selectedCompany) fetchContracts(selectedCompany, selectedStatus);
            }}
          />
        )}

        {/* ───────────────────────── BITÁCORA ───────────────────────── */}
        {view === VIEWS.LOG && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" style={{ fontWeight: 600 }}>
                  Bitácora del contrato
                </Typography>
                <Typography variant="body2" className="text-dark-black-200" style={{ marginBottom: 24 }}>
                  Modificaciones guardadas del contrato. Los nombres son de ejemplo.
                </Typography>
                <LoaderContent loading={loading} minHeight={300}>
                  {logRows.length > 0 ? (
                    <Table columns={contractLogColumns} rows={logRows} loading={false} />
                  ) : (
                    !loading && (
                      <NothingFound caption="Sin movimientos en la bitácora." subcaption="" />
                    )
                  )}
                </LoaderContent>
                <Box mt={2}>
                  <Button variant="outlined" color="primary" onClick={() => setView(VIEWS.DETAIL)}>
                    Volver al contrato
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <Toast
        {...toast.toastProps}
        duration={5000}
        onFinished={() => setToast({ toastProps: { ...toast.toastProps, open: false } })}
      />
    </>
  );
};

// ───────────────────────────────────────────────────────────────
// Detalle de contrato (con desglose o solo total) + validaciones locales.
// ───────────────────────────────────────────────────────────────
const ContractDetail = ({ classes, detail, onBack, onLog, onSaved }) => {
  const history = useHistory();
  const withDetail = detail.mode === 'detailed';
  const [total, setTotal] = React.useState(detail.purchased);
  const [lines, setLines] = React.useState(detail.lines || []);
  const [startDate, setStartDate] = React.useState(detail.start_date || '');
  const [endDate, setEndDate] = React.useState(detail.end_date || '');
  // Membresía elegida en el selector del catálogo (aún no agregada a la tabla).
  const [membershipToAdd, setMembershipToAdd] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');

  const assignedTotal = detail.assigned;
  const usagePct = detail.purchased
    ? Math.round((detail.assigned / detail.purchased) * 100)
    : 0;

  // Opciones del catálogo que todavía no están en el desglose: evita duplicar
  // una membresía ya agregada al contrato.
  const availableMemberships = React.useMemo(() => {
    const used = new Set(lines.map((l) => l.membership));
    return MEMBERSHIP_CATALOG
      .filter((m) => !used.has(m.name))
      .map((m) => ({ id: m.name, label: m.name, value: m.name }));
  }, [lines]);

  // Validación PURA: solo calcula mensajes a partir del estado. No toca estado
  // ni bloquea el tecleo; la edición de los inputs siempre es libre (Opción 1).
  const getValidationMessages = (nextTotal, nextLines) => {
    const messages = [];
    if (Number(nextTotal) < assignedTotal) {
      messages.push(
        `No se puede guardar el total ${nextTotal}. Hay ${assignedTotal} licencias asignadas. Primero hay que dar de baja licencias.`
      );
    }
    if (withDetail) {
      if (nextLines.length === 0) {
        messages.push('Agregue al menos una membresía al desglose.');
      }
      let sumPurchased = 0;
      nextLines.forEach((line) => {
        const purchased = Number(line.purchased) || 0;
        // Asignadas es dato del contrato (solo lectura): no se valida su captura,
        // solo que las compradas no queden por debajo de lo ya asignado.
        const assigned = Number(line.assigned) || 0;
        sumPurchased += purchased;
        if (purchased < 0) {
          messages.push(`${line.membership}: las compradas no pueden ser negativas.`);
        }
        if (purchased < assigned) {
          messages.push(
            `${line.membership}: las compradas (${purchased}) no pueden ser menores que las ${assigned} asignadas.`
          );
        }
      });
      if (sumPurchased !== Number(nextTotal)) {
        messages.push('La suma de las compradas del desglose debe ser igual al total general.');
      }
    }
    return messages;
  };

  // Estado derivado: los mensajes y la validez se recalculan en cada render a
  // partir de total/lines. Así el tecleo nunca se traba y Guardar se habilita
  // solo cuando todo cuadra.
  const validationMessages = getValidationMessages(total, lines);
  const isValid = validationMessages.length === 0;
  // `error` guardado se usa solo para errores de guardado del servicio.
  const displayError = error || validationMessages.join(' ');

  // Importante: identificamos la línea por `membership` (único en el desglose),
  // NO por índice. El componente Table pasa a format(row, value, index) el
  // índice de la COLUMNA, no el de la fila, así que usar índice modificaba la
  // membresía equivocada.
  const handleLineChange = (membership, field, value) => {
    setError('');
    setLines((prev) =>
      prev.map((l) => (l.membership === membership ? { ...l, [field]: value } : l))
    );
  };

  const handleAddMembership = () => {
    if (!membershipToAdd) return;
    setError('');
    setLines((prev) => [...prev, { membership: membershipToAdd, purchased: 0, assigned: 0 }]);
    setMembershipToAdd('');
  };

  const handleRemoveMembership = (membership) => {
    setError('');
    setLines((prev) => prev.filter((l) => l.membership !== membership));
  };

  const handleTotalChange = (value) => {
    setError('');
    setTotal(value);
  };

  const handleSave = async () => {
    if (!isValid) return;
    try {
      setSaving(true);
      const { message } = await updateContract(detail.id, { total, lines, withDetail });
      onSaved(message);
      onBack();
    } catch (err) {
      setError(err?.message || 'Ocurrió un error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const linesColumns = React.useMemo(
    () => [
      { name: 'membership', label: 'Membresía', minWidth: 160, align: 'center' },
      {
        name: 'purchased',
        label: 'Compradas',
        minWidth: 120,
        align: 'center',
        format: (row) => (
          <TextField
            type="number"
            variant="outlined"
            margin="dense"
            value={row.purchased === '' || row.purchased === null ? '' : row.purchased}
            onChange={(e) => handleLineChange(row.membership, 'purchased', e.target.value)}
            inputProps={{ 'aria-label': `${row.membership} compradas`, min: 0 }}
            style={{ width: 110 }}
          />
        ),
      },
      {
        // Asignadas: dato del contrato, SOLO LECTURA. No se captura a mano:
        // son licencias ya repartidas a usuarios.
        name: 'assigned',
        label: 'Asignadas',
        minWidth: 120,
        align: 'center',
        format: (row) => (
          <Typography variant="body2" style={{ fontWeight: 600, color: '#1c1c1f' }}>
            {row.assigned}
          </Typography>
        ),
      },
      {
        name: 'remove',
        label: '',
        minWidth: 90,
        align: 'center',
        format: (row) => (
          <IconButton
            size="small"
            onClick={() => handleRemoveMembership(row.membership)}
            style={{ color: '#667085' }}
            aria-label={`Quitar ${row.membership}`}
          >
            <Icon className="ri-delete-bin-line" />
          </IconButton>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines]
  );

  return (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Contrato · {detail.company_name}
          </Typography>
          <Typography variant="body2" className="text-dark-black-200" style={{ marginBottom: 24 }}>
            {withDetail
              ? 'El contrato más reciente de la empresa es el vigente. Freemium no descuenta del total.'
              : 'Contrato de solo total. Cualquier membresía standard o b2b descuenta del total.'}
          </Typography>

          {/* Métricas. Estilo explícito (fondo blanco + borde gris claro + texto
              oscuro) para que NO hereden el fondo oscuro del tema que aplica a
              Card variant="outlined". */}
          <Grid container spacing={2} className="mb-3">
            {[
              { label: 'Comprado', value: detail.purchased },
              { label: 'Asignado', value: detail.assigned },
              { label: 'Disponible', value: detail.available },
              { label: 'Freemium', value: detail.freemium },
            ].map((stat) => (
              <Grid item xs={6} md={3} key={stat.label}>
                <Box
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e6e8ee',
                    borderRadius: 12,
                    padding: '10px 14px 16px',
                  }}
                >
                  <Typography variant="caption" style={{ color: '#667085' }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="h5" style={{ fontWeight: 700, color: '#1c1c1f' }}>
                    {stat.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={3}>
            {/* Panel de edición */}
            <Grid item xs={12} md={7}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale="es">
                    <DatePicker
                      label="Inicio de vigencia"
                      name="startDate"
                      inputVariant="outlined"
                      margin="dense"
                      fullWidth
                      value={startDate ? moment(startDate, ISO_DATE_FORMAT).toDate() : null}
                      onChange={(date) =>
                        setStartDate(dateFormat(date || '', ISO_DATE_FORMAT, 0, ['DD/MM/YYYY', 'YYYY-MM-DD']))
                      }
                      format="DD/MM/YYYY"
                      placeholder="DD/MM/YYYY"
                      variant="dialog"
                      autoOk={false}
                      openTo="year"
                      views={['year', 'month', 'date']}
                      okLabel="OK"
                      cancelLabel="Cancelar"
                      TextFieldComponent={(params) => <TextField {...params} />}
                    />
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale="es">
                    <DatePicker
                      label="Fin de vigencia"
                      name="endDate"
                      inputVariant="outlined"
                      margin="dense"
                      fullWidth
                      value={endDate ? moment(endDate, ISO_DATE_FORMAT).toDate() : null}
                      onChange={(date) =>
                        setEndDate(dateFormat(date || '', ISO_DATE_FORMAT, 0, ['DD/MM/YYYY', 'YYYY-MM-DD']))
                      }
                      minDate={startDate ? moment(startDate, ISO_DATE_FORMAT).toDate() : undefined}
                      format="DD/MM/YYYY"
                      placeholder="DD/MM/YYYY"
                      variant="dialog"
                      autoOk={false}
                      openTo="year"
                      views={['year', 'month', 'date']}
                      okLabel="OK"
                      cancelLabel="Cancelar"
                      TextFieldComponent={(params) => <TextField {...params} />}
                    />
                  </MuiPickersUtilsProvider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Total general"
                    type="number"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    value={total}
                    onChange={(e) => handleTotalChange(e.target.value)}
                    inputProps={{ min: assignedTotal }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Typography variant="caption" className="text-dark-black-200">
                    Hay {assignedTotal} licencias asignadas. Un total menor no se guarda.
                  </Typography>
                </Grid>
              </Grid>

              <Box mt={2}>
                <FormControlLabel
                  control={<Checkbox checked={withDetail} disabled color="primary" />}
                  label="Capturar desglose por membresía"
                />
              </Box>

              {withDetail ? (
                <Box mt={1}>
                  {/* Selector del catálogo: elegir primero la membresía, luego
                      agregarla al desglose y capturar compradas/asignadas. */}
                  <Grid container spacing={2} alignItems="flex-end" className="mb-2">
                    <Grid item xs={12} md={8}>
                      <Select
                        items={availableMemberships}
                        label="Membresía"
                        name="membershipToAdd"
                        value={membershipToAdd}
                        onChange={(e) => setMembershipToAdd(e.target.value)}
                        disabled={!availableMemberships.length}
                        size="medium"
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleAddMembership}
                        disabled={!membershipToAdd}
                        startIcon={<Icon className="ri-add-line" />}
                        style={{ textTransform: 'none' }}
                      >
                        Agregar membresía
                      </Button>
                    </Grid>
                  </Grid>

                  {lines.length > 0 ? (
                    <Table
                      columns={linesColumns}
                      rows={lines.map((l) => ({ ...l, id: l.membership }))}
                      loading={false}
                    />
                  ) : (
                    <Typography variant="body2" className="text-dark-black-200">
                      Aún no hay membresías en el desglose. Seleccione una del catálogo y agréguela.
                    </Typography>
                  )}
                  <Typography variant="caption" className="text-dark-black-200" display="block">
                    La suma de las compradas debe ser igual al total general ({total || 0}).
                    Las asignadas son informativas (licencias ya repartidas) y no se editan.
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" className="text-dark-black-200">
                  Este contrato no especifica cantidades por membresía.
                </Typography>
              )}

              {displayError && (
                <Box
                  mt={2}
                  p={1.5}
                  style={{ backgroundColor: '#fef3f2', color: '#b42318', borderRadius: 8 }}
                  role="alert"
                >
                  <Typography variant="body2">{displayError}</Typography>
                </Box>
              )}

              <Box mt={2} display="flex" style={{ gap: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving || !isValid}
                >
                  Guardar
                </Button>
                <Button variant="outlined" onClick={onBack} className={classes.cancelButton}>
                  Cancelar
                </Button>
                <Button variant="outlined" color="primary" onClick={onLog}>
                  Bitácora
                </Button>
              </Box>
            </Grid>

            {/* Panel de uso. Mismo estilo claro que las métricas. */}
            <Grid item xs={12} md={5}>
              <Box
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e6e8ee',
                  borderRadius: 12,
                  padding: 16,
                }}
              >
                <Typography variant="h6" style={{ fontWeight: 600, color: '#1c1c1f' }}>
                  Uso del contrato vigente
                </Typography>
                <Typography variant="h4" style={{ fontWeight: 700, color: '#1c1c1f' }}>
                  {usagePct}%
                </Typography>
                <Box
                  style={{
                    height: 14,
                    background: '#e9edf5',
                    borderRadius: 999,
                    overflow: 'hidden',
                    margin: '8px 0',
                  }}
                >
                  <Box style={{ height: '100%', width: `${usagePct}%`, background: '#6236e8' }} />
                </Box>
                <Typography variant="caption" style={{ color: '#667085' }}>
                  {detail.assigned} de {detail.purchased} licencias asignadas.
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<Icon className="ri-bar-chart-line" />}
                    style={{ textTransform: 'none' }}
                    onClick={() =>
                      history.push({
                        pathname: '/b2b/contratos/consumo',
                        search: detail.company_name
                          ? `?company=${encodeURIComponent(detail.company_name)}`
                          : '',
                      })
                    }
                  >
                    Ver consumo
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );
};

// ───────────────────────────────────────────────────────────────
// Nuevo contrato (POST). Al guardarlo, queda como el vigente de la empresa.
// ───────────────────────────────────────────────────────────────
const ContractCreate = ({ classes, companyItems, onCancel, onCreated }) => {
  const [form, setForm] = React.useState({
    company: '',
    startDate: '',
    endDate: '',
    total: 100,
    withDetail: false,
  });
  const [saving, setSaving] = React.useState(false);

  const handleChange = (name) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const canSave = form.company && form.startDate && form.endDate && Number(form.total) > 0;

  const handleCreate = async () => {
    try {
      setSaving(true);
      const { message } = await createContract(form);
      onCreated(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid item xs={12}>
      <Card>
        <CardContent>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Nuevo contrato
          </Typography>
          <Typography variant="body2" className="text-dark-black-200 mb-3">
            Al guardarlo, este contrato queda como el vigente de la empresa.
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Empresa"
                fullWidth
                variant="outlined"
                margin="dense"
                value={form.company}
                onChange={handleChange('company')}
                InputLabelProps={{ shrink: true }}
              >
                {companyItems.map((c) => (
                  <MenuItem key={c.id} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale="es">
                <DatePicker
                  label="Inicio"
                  name="startDate"
                  inputVariant="outlined"
                  margin="dense"
                  fullWidth
                  value={form.startDate ? moment(form.startDate, ISO_DATE_FORMAT).toDate() : null}
                  onChange={(date) =>
                    setForm((prev) => ({
                      ...prev,
                      startDate: dateFormat(date || '', ISO_DATE_FORMAT, 0, ['DD/MM/YYYY', 'YYYY-MM-DD']) || '',
                    }))
                  }
                  format="DD/MM/YYYY"
                  placeholder="DD/MM/YYYY"
                  variant="dialog"
                  autoOk={false}
                  openTo="year"
                  views={['year', 'month', 'date']}
                  okLabel="OK"
                  cancelLabel="Cancelar"
                  TextFieldComponent={(params) => <TextField {...params} />}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale="es">
                <DatePicker
                  label="Fin"
                  name="endDate"
                  inputVariant="outlined"
                  margin="dense"
                  fullWidth
                  value={form.endDate ? moment(form.endDate, ISO_DATE_FORMAT).toDate() : null}
                  onChange={(date) =>
                    setForm((prev) => ({
                      ...prev,
                      endDate: dateFormat(date || '', ISO_DATE_FORMAT, 0, ['DD/MM/YYYY', 'YYYY-MM-DD']) || '',
                    }))
                  }
                  minDate={form.startDate ? moment(form.startDate, ISO_DATE_FORMAT).toDate() : undefined}
                  format="DD/MM/YYYY"
                  placeholder="DD/MM/YYYY"
                  variant="dialog"
                  autoOk={false}
                  openTo="year"
                  views={['year', 'month', 'date']}
                  okLabel="OK"
                  cancelLabel="Cancelar"
                  TextFieldComponent={(params) => <TextField {...params} />}
                />
              </MuiPickersUtilsProvider>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Total general"
                type="number"
                fullWidth
                variant="outlined"
                margin="dense"
                value={form.total}
                onChange={handleChange('total')}
                inputProps={{ min: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box mt={1}>
            <FormControlLabel
              control={
                <Checkbox checked={form.withDetail} onChange={handleChange('withDetail')} color="primary" />
              }
              label="Capturar desglose por membresía"
            />
            <Typography variant="caption" className="text-dark-black-200" display="block">
              Sin desglose se puede asignar cualquier membresía standard o b2b hasta el total.
              Staff y scholar no se asignan en estas pantallas.
            </Typography>
          </Box>

          <Box mt={2} display="flex" style={{ gap: 12 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreate}
              disabled={!canSave || saving}
            >
              Guardar contrato
            </Button>
            <Button variant="outlined" onClick={onCancel} className={classes.cancelButton}>
              Cancelar
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
};

const mapStateToProps = (state) => ({ ...state.userReducer });

export default connect(mapStateToProps, {})(ContractsListPage);
